import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  agentUuid,
  agentsQueryOptions,
  deleteAgent,
  duplicateAgent,
  type Agent,
} from "@/modules/chat/queries/agents-queries";
import {
  personalSpaceQueryOptions,
  spacesListQueryOptions,
} from "@/modules/spaces/spaces-queries";
import { AgentIcon } from "./AgentIcon";
import { AgentBuilder } from "./AgentBuilder";

type Tab = "all" | "personal" | "shared";
type Mode =
  | { kind: "catalogue" }
  | { kind: "new" }
  | { kind: "edit"; ref: string };

export function AgentsPage() {
  // Каталог агентов из всех пространств юзера + personal — бейдж на
  // карточке показывает где живёт агент.
  const { data: agents = [], isLoading } = useQuery(agentsQueryOptions("all"));
  const [mode, setMode] = useState<Mode>({ kind: "catalogue" });

  if (mode.kind === "new" || mode.kind === "edit") {
    const selected =
      mode.kind === "edit"
        ? agents.find((a) => a.ref === mode.ref) ?? null
        : null;
    return (
      <AgentBuilder
        mode={mode.kind}
        selected={selected}
        onClose={() => setMode({ kind: "catalogue" })}
      />
    );
  }

  return (
    <AgentsCatalogue
      agents={agents}
      isLoading={isLoading}
      onCreate={() => setMode({ kind: "new" })}
      onOpen={(ref) => setMode({ kind: "edit", ref })}
    />
  );
}

interface CatalogueProps {
  agents: Agent[];
  isLoading: boolean;
  onCreate: () => void;
  onOpen: (ref: string) => void;
}

function AgentsCatalogue({ agents, isLoading, onCreate, onOpen }: CatalogueProps) {
  const qc = useQueryClient();
  const { data: spaces = [] } = useQuery(spacesListQueryOptions());
  const { data: personalSpace } = useQuery(personalSpaceQueryOptions());
  const [tab, setTab] = useState<Tab>("all");
  const [search, setSearch] = useState("");
  const [spaceFilterId, setSpaceFilterId] = useState<string | null>(null);
  const [spaceMenuOpen, setSpaceMenuOpen] = useState(false);
  const spaceMenuRef = useRef<HTMLDivElement>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => deleteAgent(uuid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agents"] }),
  });
  const duplicateMutation = useMutation({
    mutationFn: (ref: string) => duplicateAgent(ref),
    onSuccess: (a) => {
      qc.invalidateQueries({ queryKey: ["agents"] });
      onOpen(a.ref);
    },
  });

  // «Приватный» агент = user-scope row в личном пространстве. Сравниваем
  // по id /spaces/personal — иначе свежее командное с одним участником
  // ошибочно попадёт в «Приватные» (та же ловушка, что и в HistoryView).
  const isPersonalAgent = (a: Agent) =>
    a.scope === "user" &&
    (!a.space || (!!personalSpace && a.space.id === personalSpace.id));
  const isSharedAgent = (a: Agent) =>
    a.scope === "user" && !!a.space &&
    (!personalSpace || a.space.id !== personalSpace.id);

  const filtered = useMemo(() => {
    const scoped = agents.filter((a) => {
      // Picker по конкретному пространству — системные агенты глобальные
      // и в фильтр не попадают (у них нет space).
      if (spaceFilterId && a.space?.id !== spaceFilterId) return false;
      if (tab === "personal" && !isPersonalAgent(a)) return false;
      if (tab === "shared" && !isSharedAgent(a)) return false;
      return true;
    });
    const q = search.trim().toLowerCase();
    if (!q) return scoped;
    return scoped.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.label.toLowerCase().includes(q) ||
        a.description.toLowerCase().includes(q),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents, tab, spaceFilterId, search, personalSpace]);

  // Если выбранное пространство пропало (юзер потерял доступ) — молча
  // сбрасываем фильтр на «Все», чтобы тулбар не показывал мёртвый лейбл.
  useEffect(() => {
    if (spaceFilterId && !spaces.some((s) => s.id === spaceFilterId)) {
      setSpaceFilterId(null);
    }
  }, [spaces, spaceFilterId]);

  useEffect(() => {
    if (!spaceMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (spaceMenuRef.current?.contains(e.target as Node)) return;
      setSpaceMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSpaceMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [spaceMenuOpen]);

  const spaceFilterLabel = spaceFilterId
    ? spaces.find((s) => s.id === spaceFilterId)?.name ?? "Все пространства"
    : "Все пространства";

  const sectionTitle =
    tab === "personal"
      ? "Мои агенты"
      : tab === "shared"
        ? "Командные агенты"
        : "Все агенты";

  function handleDelete(a: Agent) {
    const uuid = agentUuid(a);
    if (!uuid) return;
    if (!confirm(`Удалить агента «${a.label || a.name}»?`)) return;
    deleteMutation.mutate(uuid);
    setOpenMenu(null);
  }

  return (
    <div className="cmd-page">
      <header className="cmd-page-header">
        <h1 className="cmd-page-title">Каталог агентов</h1>
        <button type="button" className="cmd-page-create" onClick={onCreate}>
          <PlusIcon />
          <span>Создать агента</span>
        </button>
      </header>

      <div className="cmd-page-toolbar">
        <div className="history-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "all"}
            className={`history-tab${tab === "all" ? " active" : ""}`}
            onClick={() => setTab("all")}
          >
            Все
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "personal"}
            className={`history-tab${tab === "personal" ? " active" : ""}`}
            onClick={() => setTab("personal")}
          >
            Приватные
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "shared"}
            className={`history-tab${tab === "shared" ? " active" : ""}`}
            onClick={() => setTab("shared")}
          >
            Командные
          </button>
        </div>
        <div className="history-filters">
          <div className="history-filter-popover-anchor" ref={spaceMenuRef}>
            <button
              type="button"
              className={`history-filter-select${spaceFilterId ? " is-applied" : ""}${spaceMenuOpen ? " is-open" : ""}`}
              onClick={() => setSpaceMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={spaceMenuOpen}
            >
              <span className="history-filter-select-label">{spaceFilterLabel}</span>
              <Chevron />
            </button>
            {spaceMenuOpen && (
              <div className="history-filter-popover" role="menu">
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={!spaceFilterId}
                  className={`history-filter-popover-item${!spaceFilterId ? " is-active" : ""}`}
                  onClick={() => {
                    setSpaceFilterId(null);
                    setSpaceMenuOpen(false);
                  }}
                >
                  Все пространства
                </button>
                {spaces.length > 0 && <div className="history-filter-popover-sep" />}
                {spaces.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={spaceFilterId === s.id}
                    className={`history-filter-popover-item${spaceFilterId === s.id ? " is-active" : ""}`}
                    onClick={() => {
                      setSpaceFilterId(s.id);
                      setSpaceMenuOpen(false);
                    }}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="cmd-search">
            <SearchIcon />
            <input
              type="search"
              className="cmd-search-input"
              placeholder="Название агента"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <h2 className="cmd-page-section-title">{sectionTitle}</h2>

      {isLoading ? (
        <div className="cmd-empty">Загрузка…</div>
      ) : filtered.length === 0 ? (
        <div className="cmd-empty">
          {search ? (
            <p>Ничего не найдено по запросу «{search}»</p>
          ) : tab === "personal" ? (
            <>
              <p className="cmd-empty-title">Приватных агентов пока нет</p>
              <p className="cmd-empty-hint">
                Нажмите «Создать агента», чтобы добавить своего.
              </p>
            </>
          ) : tab === "shared" ? (
            <>
              <p className="cmd-empty-title">Командных агентов пока нет</p>
              <p className="cmd-empty-hint">
                Создайте агента в одном из своих пространств — он появится здесь.
              </p>
            </>
          ) : spaceFilterId ? (
            <p>В выбранном пространстве пока нет агентов.</p>
          ) : (
            <p>Агентов пока нет</p>
          )}
        </div>
      ) : (
        <div className="cmd-grid">
          {filtered.map((a) => (
            <AgentCard
              key={a.ref}
              agent={a}
              menuOpen={openMenu === a.ref}
              onMenuToggle={() =>
                setOpenMenu((m) => (m === a.ref ? null : a.ref))
              }
              onMenuClose={() => setOpenMenu(null)}
              onOpen={() => onOpen(a.ref)}
              onDuplicate={() => duplicateMutation.mutate(a.ref)}
              onDelete={() => handleDelete(a)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface CardProps {
  agent: Agent;
  menuOpen: boolean;
  onMenuToggle: () => void;
  onMenuClose: () => void;
  onOpen: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function AgentCard({
  agent,
  menuOpen,
  onMenuToggle,
  onMenuClose,
  onOpen,
  onDuplicate,
  onDelete,
}: CardProps) {
  const isSystem = agent.scope === "system";
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) onMenuClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onMenuClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen, onMenuClose]);

  const scopeBadge = isSystem
    ? "Системный"
    : agent.space?.name ?? "Личное";

  return (
    <div className="cmd-card">
      <div className="cmd-card-head">
        <div className="cmd-card-head-main">
          <AgentIcon size={28} variant={agent.scope} />
          <span className="cmd-card-name">{agent.label || agent.name}</span>
          <span className="cmd-card-badge">{scopeBadge}</span>
        </div>
        <div className="cmd-card-menu-wrap" ref={wrapRef}>
          <button
            type="button"
            className="cmd-card-kebab"
            onClick={onMenuToggle}
            aria-label="Действия"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <KebabIcon />
          </button>
          {menuOpen && (
            <div className="cmd-card-menu" role="menu">
              {isSystem ? (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    className="cmd-card-menu-item"
                    onClick={() => {
                      onMenuClose();
                      onOpen();
                    }}
                  >
                    <EyeIcon />
                    <span>Открыть</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="cmd-card-menu-item"
                    onClick={() => {
                      onMenuClose();
                      onDuplicate();
                    }}
                  >
                    <CopyIcon />
                    <span>Сделать копию</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    className="cmd-card-menu-item"
                    onClick={() => {
                      onMenuClose();
                      onOpen();
                    }}
                  >
                    <EditIcon />
                    <span>Редактировать</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="cmd-card-menu-item"
                    onClick={() => {
                      onMenuClose();
                      onDuplicate();
                    }}
                  >
                    <CopyIcon />
                    <span>Дублировать</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="cmd-card-menu-item cmd-card-menu-item--danger"
                    onClick={() => {
                      onMenuClose();
                      onDelete();
                    }}
                  >
                    <TrashIcon />
                    <span>Удалить</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <p className="cmd-card-desc">{agent.description || "Без описания"}</p>
    </div>
  );
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="m11 11 3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function KebabIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <circle cx="8" cy="3.5" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="8" cy="12.5" r="1.4" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M8 3.5C4.5 3.5 1.83 6.04 1 8c.83 1.96 3.5 4.5 7 4.5s6.17-2.54 7-4.5c-.83-1.96-3.5-4.5-7-4.5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M14.1666 3.21354C14.1667 2.84774 14.021 2.49697 13.7623 2.23828C13.5038 1.9797 13.1534 1.83411 12.7877 1.83398C12.4221 1.83394 12.0711 1.97918 11.8125 2.23763L10.7116 3.33854L12.6621 5.28906L13.7623 4.1888C14.021 3.9302 14.1666 3.5793 14.1666 3.21354ZM2.91469 11.1374C2.81831 11.2335 2.74583 11.3517 2.7057 11.4818L2.70635 11.4824L1.91599 14.0833L4.51885 13.2943C4.64934 13.2546 4.76861 13.1835 4.86521 13.0872L11.955 5.99609L10.0045 4.04557L2.91469 11.1374ZM15.1666 3.21354C15.1666 3.80518 14.9461 4.37413 14.5508 4.81055L14.4694 4.89583L13.069 6.29557C13.0541 6.31581 13.0385 6.33586 13.0202 6.35417C13.0018 6.37247 12.9818 6.38812 12.9616 6.40299L5.57094 13.7956C5.38501 13.9809 5.16184 14.1242 4.91664 14.2155L4.80987 14.2513L1.90752 15.1315H1.90622C1.76223 15.1747 1.60931 15.1786 1.46351 15.1419C1.31762 15.1052 1.18456 15.0295 1.0781 14.9232C0.971689 14.8169 0.895629 14.6842 0.858696 14.5384C0.821783 14.3926 0.825387 14.2392 0.868462 14.0951L0.869113 14.0931L1.74932 11.1914L1.74997 11.1901C1.83808 10.9029 1.99556 10.6418 2.20831 10.4297L9.59698 3.03841C9.61183 3.01821 9.62818 2.99874 9.64646 2.98047C9.66473 2.9622 9.6842 2.94584 9.7044 2.93099L11.1048 1.5306C11.551 1.08446 12.1567 0.833905 12.7877 0.833984C13.4187 0.834109 14.0239 1.08504 14.47 1.53125C14.9161 1.97749 15.1667 2.58261 15.1666 3.21354Z" />
    </svg>
  );
}

function Chevron() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="5" y="5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2h-6A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5"
        stroke="currentColor"
        strokeWidth="1.4"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" clipRule="evenodd" d="M9.33301 0.832031C9.82896 0.832031 10.2833 1.07533 10.6035 1.39551C10.9237 1.71568 11.167 2.17007 11.167 2.66602V3.49902H14C14.2761 3.49902 14.5 3.72288 14.5 3.99902C14.5 4.27517 14.2761 4.49902 14 4.49902H13.167V13.332C13.167 13.828 12.9237 14.2824 12.6035 14.6025C12.2833 14.9227 11.829 15.166 11.333 15.166H4.66699C4.17104 15.166 3.71666 14.9227 3.39648 14.6025C3.07631 14.2824 2.83301 13.828 2.83301 13.332V4.49902H2C1.72386 4.49902 1.5 4.27517 1.5 3.99902C1.5 3.72288 1.72386 3.49902 2 3.49902H4.83301V2.66602C4.83301 2.17007 5.07631 1.71568 5.39648 1.39551C5.71666 1.07533 6.17104 0.832031 6.66699 0.832031H9.33301ZM3.83301 13.332C3.83301 13.5027 3.92369 13.7157 4.10352 13.8955C4.28334 14.0753 4.49627 14.166 4.66699 14.166H11.333C11.5037 14.166 11.7167 14.0753 11.8965 13.8955C12.0763 13.7157 12.167 13.5027 12.167 13.332V4.49902H3.83301V13.332ZM6.66699 6.83203C6.94288 6.83221 7.16682 7.05615 7.16699 7.33203V11.332C7.16699 11.6081 6.94299 11.8319 6.66699 11.832C6.39085 11.832 6.16699 11.6082 6.16699 11.332V7.33203C6.16717 7.05604 6.39096 6.83203 6.66699 6.83203ZM9.33301 6.83203C9.60904 6.83203 9.83283 7.05604 9.83301 7.33203V11.332C9.83301 11.6082 9.60915 11.832 9.33301 11.832C9.05702 11.8319 8.83301 11.6081 8.83301 11.332V7.33203C8.83318 7.05615 9.05712 6.83221 9.33301 6.83203ZM6.66699 1.83203C6.49627 1.83203 6.28334 1.92271 6.10352 2.10254C5.92369 2.28237 5.83301 2.4953 5.83301 2.66602V3.49902H10.167V2.66602C10.167 2.4953 10.0763 2.28237 9.89648 2.10254C9.71666 1.92271 9.50373 1.83203 9.33301 1.83203H6.66699Z" />
    </svg>
  );
}
